import { IllumiLogoMark } from "@src/components/illumi-logo-mark";
import { useEffect, useState } from "react";
import type React from "react";
import {
	type DesktopBrowserLoginState,
	cancelDesktopBrowserLogin,
	desktopBrowserLoginSignal,
	dismissDesktopBrowserLoginOverlay,
	retryDesktopBrowserLogin,
} from "./desktop-browser-login";

/**
 * Full-viewport waiting / error UI for desktop system-browser Auth0 login.
 * Mounted at the app root so it is visible while route loaders are pending.
 *
 * Uses native buttons (not Mantine) because this overlay sits outside
 * AuthenticatedLayout’s MantineProvider.
 */
export function DesktopBrowserLoginOverlay(): React.ReactElement | null {
	const [state, setState] = useState<DesktopBrowserLoginState>(
		() => desktopBrowserLoginSignal.value,
	);

	useEffect(() => {
		return desktopBrowserLoginSignal.subscribe((next) => {
			setState(next);
		});
	}, []);

	if (state.status === "idle") {
		return null;
	}

	const isWaiting = state.status === "waiting";
	const isError = state.status === "error";
	const isCancelled = state.status === "cancelled";

	const title = isWaiting
		? "Continue in your browser"
		: isCancelled
			? "Login cancelled"
			: "Login failed";

	const message = isWaiting
		? "Complete sign-in in the browser window that just opened, then return here."
		: isCancelled
			? "You can try again whenever you're ready."
			: (state.error ?? "Something went wrong during browser login.");

	const handleCancel = () => {
		if (isWaiting) {
			void cancelDesktopBrowserLogin();
			return;
		}
		dismissDesktopBrowserLoginOverlay();
	};

	const handleRetry = () => {
		void retryDesktopBrowserLogin();
	};

	return (
		<dialog
			open
			className="fixed inset-0 z-100000 m-0 flex h-dvh max-h-none w-screen max-w-none flex-col items-center justify-center border-0 bg-surface-app px-6"
			aria-labelledby="desktop-browser-login-title"
			aria-modal="true"
			aria-busy={isWaiting}
		>
			{isWaiting && <IllumiLogoMark animated className="mb-8" />}

			<h1
				id="desktop-browser-login-title"
				className="mb-4 text-center text-2xl font-bold text-text-primary"
			>
				{title}
			</h1>
			<p className="mb-10 max-w-md text-center text-lg text-muted">{message}</p>

			<div className="flex flex-col gap-2 md:flex-row md:gap-4">
				<button
					type="button"
					className="min-w-30 cursor-pointer rounded-sm border border-border-default bg-transparent px-6 py-2.5 text-lg font-medium text-text-primary hover:bg-surface-subtle"
					onClick={handleCancel}
				>
					{isWaiting ? "Cancel" : "Close"}
				</button>

				{(isError || isCancelled) && (
					<button
						type="button"
						className="min-w-30 cursor-pointer rounded-sm bg-brand-primary px-6 py-2.5 text-lg font-medium text-text-inverse hover:bg-brand-primary-hover"
						onClick={handleRetry}
					>
						Retry
					</button>
				)}
			</div>
		</dialog>
	);
}
